// Hub ▸ Marketing ▸ Overview — the CEO scorecard (slice S2, CEO 2026-10-21).
//
// One basis for every spend decision (CEO, S2 review 2): gross profit
// CREDITED TO THE LAST AD CLICKED ÷ ad spend (sample packs included). The
// verdict, the POAS hero, the channel scorecard's Signal and the campaigns all
// use it, and say so. The view "by how clients found us" (lead source) is a
// labelled second view — a toggle on the scorecard, one supporting tile, the
// weekly trend — never mixed into a decision column.
//
// Marketing-only viewers (CEO 2026-10-22) get the same page in REVENUE: ROAS
// instead of POAS, revenue credited to ads, revenue from marketing — the API
// sends them no profit field (measure.ts picks the words and columns).
//
// Order: verdict → the line that reconciles the two gross-profit numbers →
// hero row (POAS with the exact numbers that divide, gross profit credited to
// ads, ad spend vs budget) → channel scorecard → supporting numbers → trend →
// top campaigns. Every number shows its formula (ⓘ) and opens its rows.
import { useCallback, useState } from "react";
import { Link, useMatch, useSearchParams } from "react-router-dom";
import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";
import { Bullet, Legend, SERIES, TrendChart } from "./charts";
import { DrillSheet } from "./DrillSheet";
import {
  EmptyState, ErrorLine, FreshnessStamp, InfoTip, Num, Panel, PeriodPicker, Pill, Segmented, Skeleton, SortTable, count, fmtValue, money, periodQuery, useApi,
  type PeriodState, type Tone,
} from "./kit";
import { parsePeriodParams } from "./period";
import { RATIO_BARS, WORDS, ratioOf, signalRule, valueOf } from "./measure";
import type { Measure, OverviewReady, OverviewResponse, PerfRow, ScoreRow, Signal, Tile } from "./types";

/** The dates in the address (?period=…), validated — anything odd → this month. */
export function usePeriodParam(): [PeriodState, (p: PeriodState) => void] {
  const [sp, setSp] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  const p = parsePeriodParams((k) => sp.get(k), today);
  return [p, (n) => {
    const next = new URLSearchParams(sp);
    next.set("period", n.preset);
    if (n.preset === "custom") { next.set("from", n.from); next.set("to", n.to); } else { next.delete("from"); next.delete("to"); }
    setSp(next, { replace: true });
  }];
}

const bandTone = (b: Tile["band"]): Tone => (!b ? "neutral" : b.band === "losing" ? "risk" : b.band === "strong" ? "ok" : "neutral");
const LAST = "last ad clicked";

function Delta({ tile }: { tile: Tile }) {
  if (tile.value === null || tile.compareValue === null) return <p className="text-label text-ink-muted">Nothing to compare with</p>;
  const diff = tile.value - tile.compareValue;
  const before = <span className="block font-normal text-ink-muted">before: {fmtValue(tile.unit, tile.compareValue)}</span>;
  if (diff === 0) return <p className="text-label text-ink-muted"><span className="flex items-center gap-1"><ArrowRight className="h-4 w-4" aria-hidden />Same as before</span>{before}</p>;
  const pct = tile.compareValue !== 0 ? Math.abs(diff / tile.compareValue) : null;
  const up = diff > 0;
  const good = tile.higherIsBetter === null ? null : up === tile.higherIsBetter;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const amount = tile.unit === "currency_cents" ? money(Math.abs(diff)) : tile.unit === "ratio" ? `${Math.abs(diff).toFixed(2)}×` : count(Math.abs(diff));
  return (
    <p className={cn("text-label font-semibold", good === null ? "text-ink" : good ? "text-ok" : "text-risk")}>
      <span className="flex items-center gap-1"><Icon className="h-4 w-4" aria-hidden />{up ? "Up" : "Down"} {pct !== null ? `${Math.round(pct * 100)}%` : amount}{good !== null && <span className="sr-only">{good ? " (better)" : " (worse)"}</span>}</span>
      {before}
    </p>
  );
}

function TileBody({ tile }: { tile: Tile }) {
  const budget = tile.id === "spend" ? tile.parts.budget_cents ?? null : null;
  return (
    <>
      <span className="block min-h-[44px] text-label font-semibold leading-snug text-ink">{tile.label}{tile.basis && <span className="block text-meta font-normal text-ink-muted">{tile.basis}</span>}</span>
      <span className={cn("mt-1 block font-semibold leading-none tracking-tight text-ink", tile.value === null && "text-ink-muted")}
        style={{ fontSize: tile.hero ? "clamp(44px, 5vw, 64px)" : "clamp(30px, 3vw, 40px)" }}>
        {fmtValue(tile.unit, tile.value)}
      </span>
      <span className="mt-3 flex flex-1 flex-col justify-end gap-2">
        {tile.band && <span><Pill tone={bandTone(tile.band)}>{tile.band.words}</Pill></span>}
        {tile.value === null && tile.why && <span className="text-label leading-snug text-ink-muted">{tile.why}</span>}
        {budget !== null && budget > 0 && tile.value !== null && (
          <Bullet value={tile.value} target={budget} track={budget} over={tile.value > budget * 1.1}
            label={`${money(tile.value)} spent of ${money(budget)} budget${tile.value > budget * 1.1 ? " — over budget" : ""}`} />
        )}
        {tile.sub && <span className={cn("text-label", tile.id === "poas" || tile.id === "roas" ? "font-semibold text-ink" : "text-ink-muted")}>{tile.sub}</span>}
        <Delta tile={tile} />
        <span className="text-meta font-semibold text-ink underline decoration-rule underline-offset-4 group-hover:decoration-ink">{tile.metric ? "See the rows →" : "See the campaigns →"}</span>
      </span>
    </>
  );
}

function TileCard({ tile, onOpen, channelsHref }: { tile: Tile; onOpen: (t: Tile) => void; channelsHref: string }) {
  const formula = [tile.formula, ...tile.extra].join(" ");
  const cls = "group flex h-full flex-col p-4 pr-11 text-left hover:bg-[#FAFAF8] sm:p-5 sm:pr-12";
  return (
    <div className={cn("relative flex min-w-0 flex-col rounded-lg border bg-white", tile.hero ? "border-ink/20" : "border-rule")}>
      {tile.metric
        ? <button type="button" onClick={() => onOpen(tile)} aria-label={`${tile.label}: ${fmtValue(tile.unit, tile.value)}. See the rows`} className={cls}><TileBody tile={tile} /></button>
        : <Link to={channelsHref} aria-label={`${tile.label}: ${fmtValue(tile.unit, tile.value)}. See the campaigns`} className={cls}><TileBody tile={tile} /></Link>}
      <span className="absolute right-2 top-2 sm:right-3 sm:top-3"><InfoTip label={tile.label} formula={formula} notes={tile.notes} align="right" /></span>
    </div>
  );
}

const SIGNAL_TONE: Record<Signal, Tone> = { scale: "ok", hold: "neutral", cut: "risk", watch: "neutral", too_early: "warn", no_spend: "neutral", not_creditable: "neutral" };
/** POAS (admin + finance) or ROAS (marketing-only), coloured by that measure's own bars. */
function RatioCell({ v, spend, value, m }: { v: number | null; spend: number; value: number | null; m: Measure }) {
  // No ratio (sample packs) → "—" first; never "$0 back" for what can't be credited.
  if (v === null) return <span className="text-ink-muted">—</span>;
  if (value !== null && spend > 0 && value === 0) return <span className="font-semibold text-risk">$0 back</span>;
  const b = RATIO_BARS[m];
  return <span className={cn(v < b.low ? "font-semibold text-risk" : v >= b.high ? "font-semibold text-ok" : "")}>{v.toFixed(2)}×</span>;
}
function SignalCell({ r }: { r: ScoreRow }) {
  return <span className="inline-flex flex-col items-start gap-0.5"><Pill tone={SIGNAL_TONE[r.signal]}>{r.signalWords}</Pill>{r.reason && <span className="text-meta text-ink-muted">{r.reason}</span>}</span>;
}

/** Phones: one card per channel — POAS and Signal always visible. */
function ScoreCards({ rows, decision, m }: { rows: ScoreRow[]; decision: boolean; m: Measure }) {
  return (
    <ul className="space-y-2 sm:hidden">
      {rows.map((r) => (
        <li key={r.channel} className="rounded-md border border-rule p-3">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-ink">{r.label}</p>
            {decision ? <RatioCell v={ratioOf(r, m)} spend={r.spendCents} value={valueOf(r, m)} m={m} /> : <span className="tabular-nums">{money(valueOf(r, m))}</span>}
          </div>
          {decision && <div className="mt-1"><SignalCell r={r} /></div>}
          <dl className="mt-2 grid grid-cols-3 gap-2 text-label">
            {(decision
              ? [["Spend", money(r.spendCents)], [WORDS[m].value, money(valueOf(r, m))], ["Budget left", r.budgetLeftCents === null ? "—" : r.budgetLeftCents < 0 ? `${money(-r.budgetLeftCents)} over` : money(r.budgetLeftCents)]]
              : [["New clients", count(r.newCustomers)], ["Paid orders", count(r.orders)]]).map(([k, v]) => (
              <div key={k}><dt className="text-meta text-ink-muted">{k}</dt><dd className="tabular-nums">{v}</dd></div>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

function Scorecard({ d }: { d: OverviewReady }) {
  const [view, setView] = useState<"last" | "found">("last");
  const m = d.measure; const W = WORDS[m]; const bars = RATIO_BARS[m];
  const gpLabel = view === "last" ? `${W.value} (${LAST})` : `${W.value} (how clients found us)`;
  const decisionCols = [
    { key: "label", label: "Channel", value: (r: ScoreRow) => r.label, render: (r: ScoreRow) => <span className="font-semibold">{r.label}</span> },
    { key: "spend", label: "Spend", numeric: true, value: (r: ScoreRow) => r.spendCents, render: (r: ScoreRow) => <Num v={r.spendCents}>{money(r.spendCents)}</Num> },
    { key: "gp", label: gpLabel, numeric: true, info: `${W.value} of paid orders ${d.bases.lastClick}. Sample packs can't be credited to a click.`, value: (r: ScoreRow) => valueOf(r, m), render: (r: ScoreRow) => <Num v={valueOf(r, m)}>{money(valueOf(r, m))}</Num> },
    { key: "poas", label: `${W.ratio} (${LAST})`, numeric: true, info: m === "revenue" ? `Revenue credited to the channel ÷ its spend. Below ${bars.low.toFixed(2)} is under target; ${bars.high.toFixed(2)} and up is strong.` : "Gross profit credited to the channel ÷ its spend. Below 1.00 it loses money; 2.00 and up is strong.", value: (r: ScoreRow) => ratioOf(r, m), render: (r: ScoreRow) => <RatioCell v={ratioOf(r, m)} spend={r.spendCents} value={valueOf(r, m)} m={m} /> },
    { key: "cac", label: "Cost per new client", numeric: true, info: `Spend ÷ new clients whose first order was credited to the channel (${LAST}).`, value: (r: ScoreRow) => r.cacCents, render: (r: ScoreRow) => <Num v={r.cacCents}>{money(r.cacCents)}</Num> },
    { key: "left", label: "Budget left (this month)", numeric: true, info: "This month's plan for the line minus what it has spent so far.", value: (r: ScoreRow) => r.budgetLeftCents, render: (r: ScoreRow) => <Num v={r.budgetLeftCents}><span className={cn(r.budgetLeftCents !== null && r.budgetLeftCents < 0 && "font-semibold text-risk")}>{r.budgetLeftCents !== null && r.budgetLeftCents < 0 ? `${money(-r.budgetLeftCents)} over` : money(r.budgetLeftCents)}</span></Num> },
    { key: "signal", label: "Signal", info: signalRule(m), value: (r: ScoreRow) => r.signalWords, render: (r: ScoreRow) => <SignalCell r={r} /> },
  ];
  const foundCols = [
    { key: "label", label: "How clients found us", value: (r: ScoreRow) => r.label, render: (r: ScoreRow) => <span className="font-semibold">{r.label}</span> },
    { key: "gp", label: gpLabel, numeric: true, info: `${W.value} of orders paid in these dates by clients whose lead source is this channel — every order, ad click or not.`, value: (r: ScoreRow) => valueOf(r, m), render: (r: ScoreRow) => <Num v={valueOf(r, m)}>{money(valueOf(r, m))}</Num> },
    { key: "new", label: "New clients", numeric: true, value: (r: ScoreRow) => r.newCustomers, render: (r: ScoreRow) => <Num v={r.newCustomers}>{count(r.newCustomers)}</Num> },
    { key: "orders", label: "Paid orders", numeric: true, value: (r: ScoreRow) => r.orders, render: (r: ScoreRow) => <Num v={r.orders}>{count(r.orders)}</Num> },
  ];
  const s = d.scorecard;
  return (
    <Panel title="Channel scorecard" caption={view === "last" ? s.caption : "Counted by how each client first found us (their lead source). For context only — spend decisions use the last ad clicked."}
      info={<InfoTip label="the scorecard" formula={`The decision view credits each paid order to the last ad clicked before it (the same rules as Channels & Campaigns${m === "revenue" ? "" : " and Flow ▸ Reports ▸ Advertising"}). The second view counts every order by how the client first found us.`} />}
      actions={<Segmented label="Basis" value={view} onChange={setView} options={[{ value: "last", label: "Credited to the last ad clicked" }, { value: "found", label: "By how clients found us" }]} />}>
      {view === "last" ? (
        <div className="space-y-5">
          {s.rows.length ? <>
            <div className="hidden sm:block"><SortTable caption="Channel scorecard, last ad clicked" rows={s.rows} rowKey={(r) => r.channel} columns={decisionCols} initialSort={{ key: "poas", dir: "desc" }} stickyFirst minWidth={940} /></div>
            <ScoreCards rows={s.rows} decision m={m} />
          </> : <EmptyState title="No ad spend or credited orders in these dates">Rows appear once ad spend arrives or an order follows an ad click.</EmptyState>}
          {s.notAds.length > 0 && (
            <div>
              <h3 className="text-label font-semibold text-ink">Orders that didn't follow an ad click</h3>
              <p className="text-meta text-ink-muted">Organic search, direct visits, email and other websites — {W.valueLower} {LAST} without any spend to judge.</p>
              <div className="mt-2 hidden sm:block"><SortTable caption="Orders not from an ad" rows={s.notAds} rowKey={(r) => r.channel} columns={[decisionCols[0]!, decisionCols[2]!, { key: "orders", label: "Paid orders", numeric: true, value: (r: ScoreRow) => r.orders, render: (r: ScoreRow) => <Num v={r.orders}>{count(r.orders)}</Num> }]} initialSort={{ key: "gp", dir: "desc" }} stickyFirst minWidth={520} /></div>
              <div className="mt-2"><ScoreCards rows={s.notAds} decision={false} m={m} /></div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="hidden sm:block"><SortTable caption="By how clients found us — marketing" rows={d.foundUs.marketing} rowKey={(r) => r.channel} columns={foundCols} initialSort={{ key: "gp", dir: "desc" }} stickyFirst minWidth={620} /></div>
          <ScoreCards rows={d.foundUs.marketing} decision={false} m={m} />
          {d.foundUs.other.length > 0 && (
            <div>
              <h3 className="text-label font-semibold text-ink">Not from marketing</h3>
              <p className="text-meta text-ink-muted">Referrals, outbound sales, clients returning from commonsku, other and unknown sources.</p>
              <div className="mt-2 hidden sm:block"><SortTable caption="By how clients found us — not marketing" rows={d.foundUs.other} rowKey={(r) => r.channel} columns={foundCols} initialSort={{ key: "gp", dir: "desc" }} stickyFirst minWidth={620} /></div>
              <div className="mt-2"><ScoreCards rows={d.foundUs.other} decision={false} m={m} /></div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function CampaignCards({ rows, m }: { rows: PerfRow[]; m: Measure }) {
  return (
    <ul className="space-y-2 sm:hidden">
      {rows.map((r) => (
        <li key={`${r.channel}|${r.campaign}`} className="rounded-md border border-rule p-3">
          <div className="flex items-start justify-between gap-2">
            <p><span className="font-semibold text-ink">{r.campaign ?? "(no campaign)"}</span><span className="block text-meta text-ink-muted">{r.channelLabel}</span></p>
            <RatioCell v={ratioOf(r, m)} spend={r.spendCents} value={valueOf(r, m)} m={m} />
          </div>
          <dl className="mt-2 grid grid-cols-4 gap-2 text-label">
            {[["Spend", money(r.spendCents)], ["Leads", count(r.leads)], ["Orders", count(r.orders)], [WORDS[m].value, money(valueOf(r, m))]].map(([k, v]) => <div key={k}><dt className="text-meta text-ink-muted">{k}</dt><dd className="tabular-nums">{v}</dd></div>)}
          </dl>
        </li>
      ))}
    </ul>
  );
}

function Campaigns({ rows, formulas, m }: { rows: PerfRow[]; formulas: Record<string, string>; m: Measure }) {
  return (
    <>
      <div className="hidden sm:block">
        <SortTable caption="Top campaigns" rows={rows} rowKey={(r) => `${r.channel}|${r.campaign}`} initialSort={{ key: "gp", dir: "desc" }} stickyFirst minWidth={780}
          columns={[
            { key: "campaign", label: "Campaign", value: (r) => r.campaign, render: (r) => <><span className="font-semibold">{r.campaign ?? "(no campaign)"}</span><span className="block text-meta text-ink-muted">{r.channelLabel}</span></> },
            { key: "spend", label: "Spend", numeric: true, info: formulas.spend, value: (r) => r.spendCents, render: (r) => <Num v={r.spendCents}>{money(r.spendCents)}</Num> },
            { key: "leads", label: "Leads", numeric: true, info: formulas.leads, value: (r) => r.leads, render: (r) => <Num v={r.leads}>{count(r.leads)}</Num> },
            { key: "orders", label: "Orders", numeric: true, info: formulas.orders, value: (r) => r.orders, render: (r) => <Num v={r.orders}>{count(r.orders)}</Num> },
            { key: "gp", label: `${WORDS[m].value} (${LAST})`, numeric: true, info: m === "revenue" ? formulas.revenue : formulas.gp, value: (r) => valueOf(r, m), render: (r) => <Num v={valueOf(r, m)}>{money(valueOf(r, m))}</Num> },
            { key: "poas", label: `${WORDS[m].ratio} (${LAST})`, numeric: true, info: m === "revenue" ? formulas.roas : formulas.poas, value: (r) => ratioOf(r, m), render: (r) => <RatioCell v={ratioOf(r, m)} spend={r.spendCents} value={valueOf(r, m)} m={m} /> },
          ]} />
      </div>
      <CampaignCards rows={rows} m={m} />
    </>
  );
}

function OverviewBody({ d, onDrill, channelsHref }: { d: OverviewReady; onDrill: (t: Tile) => void; channelsHref: string }) {
  const m = d.measure; const W = WORDS[m];
  const hasTrend = d.trend.some((w) => w.spendCents || valueOf(w, m));
  const hero = d.tiles.filter((t) => t.hero);
  const rest = d.tiles.filter((t) => !t.hero);
  const poas = hero.find((t) => t.id === "poas" || t.id === "roas");
  const ratioNote = d.ltv ?? d.firstYear ?? null;
  return (
    <div className="space-y-6">
      <div className="max-w-5xl space-y-2">
        <p className={cn("text-[22px] font-semibold leading-snug sm:text-section", poas?.band?.band === "losing" ? "text-risk" : "text-ink")}>{d.verdict}</p>
        <p className="text-label text-ink-muted">{d.reconciliation.text}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-3">
        {hero.map((t) => <TileCard key={t.id} tile={t} onOpen={onDrill} channelsHref={channelsHref} />)}
      </div>

      <Scorecard d={d} />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-6">
        {rest.map((t) => <TileCard key={t.id} tile={t} onOpen={onDrill} channelsHref={channelsHref} />)}
      </div>
      <div className="space-y-1 text-label text-ink-muted">
        <p>{d.basis}</p>
        {ratioNote && <p className="flex items-center gap-1">{ratioNote.text}<InfoTip label={m === "revenue" ? "first-year revenue : cost to win" : "LTV : CAC"} formula={ratioNote.formula} /></p>}
      </div>

      <Panel title={`Ad spend vs ${W.valueLower}, by week`} caption={`${W.value} here is ${d.bases.foundUs}: every order from a client who first found us through marketing.`}
        info={<InfoTip label="this chart" formula={d.trendFormula} />}
        actions={<Legend items={[{ label: `${W.value} (how clients found us)`, color: SERIES.gp, shape: "line" }, { label: "Ad spend", color: SERIES.spend, shape: "dashed" }]} />}>
        {hasTrend ? <TrendChart data={d.trend} word={W.valueLower} /> : (
          <EmptyState title={`No spend or marketing ${W.valueLower} in these dates yet`}>
            Weekly lines appear once ad spend arrives (Google and Microsoft automatically; LinkedIn, Meta, ChatGPT and sample packs by upload on Channels &amp; Campaigns) and orders from marketing clients are paid.
          </EmptyState>
        )}
      </Panel>

      <Panel title="Top campaigns" caption={`By ${W.valueLower} ${d.bases.lastClick}. The full table is on Channels & Campaigns.`}
        info={<InfoTip label="top campaigns" formula={`${d.perfFormulas.model ?? ""} Spend is the same spend the numbers above add up.`} />}>
        {d.campaignNotes.map((n) => <p key={n} className="mb-3 text-label text-warn">{n}</p>)}
        {d.campaigns.length ? <Campaigns rows={d.campaigns} formulas={d.perfFormulas} m={m} /> : (
          <EmptyState title="No campaigns in these dates">Campaigns appear once ad spend arrives or a website visit with a campaign tag turns into an order.</EmptyState>
        )}
      </Panel>
    </div>
  );
}

export function OverviewTab() {
  const [period, setPeriod] = usePeriodParam();
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes("admin");
  const base = useMatch("/marketing/*")?.pathnameBase ?? "/marketing";
  const channelsHref = `${base}/channels?${period.preset === "custom" ? `period=custom&from=${period.from}&to=${period.to}` : `period=${period.preset}`}`;
  const { data, error, loading, reload } = useApi<OverviewResponse>(`/api/marketing/overview?${periodQuery(period)}`);
  const [drill, setDrill] = useState<Tile | null>(null);
  const closeDrill = useCallback(() => setDrill(null), []);
  const ready = data && data.ready ? data : null;
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <PeriodPicker value={period} onChange={setPeriod} resolved={ready?.period} compare={ready?.compare} />
        {ready && <FreshnessStamp freshness={ready.freshness} isAdmin={isAdmin} onRefreshed={reload} throughDay={ready.throughDay} />}
      </div>
      {error && <ErrorLine onRetry={reload}>{error}</ErrorLine>}
      {loading && !data && <Skeleton tiles={6} rows={2} label="Adding up the numbers" />}
      {data && !data.ready && <EmptyState title="The scorecard isn't switched on yet">{data.detail} The Email and Social tabs work as before.</EmptyState>}
      {ready && <div className={cn("transition-opacity", loading && "opacity-60")}><OverviewBody d={ready} onDrill={setDrill} channelsHref={channelsHref} /></div>}
      {drill && drill.metric && ready && <DrillSheet metric={drill.metric} label={drill.label} from={ready.period.from} to={ready.period.to} onClose={closeDrill} />}
    </div>
  );
}
